import { Trans, useTranslation } from "react-i18next";
import { ChartDonut, ChartThemeColor, ChartTooltip } from "@patternfly/react-charts";
import "./DonutProgressCircle.scss";
import { useRef } from "react";
import defaultContent from "../../../content/ssiUplineReport";
import { useLeafVolumeLabel } from "../../../common/leaf/LeafVolumeLabel/LeafVolumeLabel";
import { VolumeRequirementLoader } from "../../../common/components/RequirementTiles/VolumeRequirementTile/Loader/VolumeRequirementLoader";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { REQUIREMENT_NAME } from "../../../common/enums/requirements";

export type FormattedLabelValues = {
  currentAmount?: string;
  targetAmount?: string;
};

export type Props = {
  label: string;
  percent?: boolean;
  ariaTitle: string;
  currentAmount?: number;
  currentAmountFormatted?: string;
  targetAmount?: number;
  targetAmountFormatted?: string;
  totalSegments?: number;
  filledSegments?: number;
  isLoading?: boolean;
  animationDuration?: number;
  met?: boolean;
};

export type DonutChartSlice = { x: number; y: number };

/** Builds the two donut slices (progress + remainder), guarding against negative remainders when actual exceeds target. */
export const getDonutChartData = (currentAmount: number, targetAmount: number, met: boolean): DonutChartSlice[] => [
  { x: 0, y: targetAmount === currentAmount || met ? 100 : currentAmount },
  { x: 100, y: met ? 0 : Math.max(0, targetAmount - currentAmount) },
];

const DonutProgressCircle = (props: Props) => {
  const { t } = useTranslation(["ssiUplineReport"]);
  const {
    label,
    percent = false,
    currentAmount = 0,
    currentAmountFormatted,
    targetAmount = 0,
    targetAmountFormatted,
    isLoading = false,
    animationDuration = 0,
    ariaTitle,
    met = false,
  } = props;
  const ref = useRef(null);

  const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);
  const percentage = currentAmount / targetAmount;
  const currentPercentage = met ? 1 : clamp(percentage, 0, 1);
  const greyColor = "#E4E4E4";
  const orangeColor = "#DA7600";
  const greenColor = "#107F47";

  const data = getDonutChartData(currentAmount, targetAmount, met);

  const getTitle = useLeafVolumeLabel();

  if (isLoading) {
    return (
      <div className="DonutProgressCircle">
        <div className="DonutProgressCircle__Loader">
          <VolumeRequirementLoader border={false} showHeader={false} />
        </div>
        <div className="DonutProgressCircle__label" data-testid={"DonutProgressCircle-label"}>
          <div className="DonutProgressCircle__label--title">
            <LeafRectangleLoader height="1rem" width="3rem" isLoading margin="0" />
          </div>
          <div className="DonutProgressCircle__label--text">
            <LeafRectangleLoader height=".75rem" width="4rem" isLoading margin=".5rem 0 0 0" />
          </div>
        </div>
      </div>
    );
  }

  const animationSettings = {
    duration: animationDuration,
    onEnter: {
      duration: animationDuration,
    },
    onExit: {
      duration: animationDuration,
    },
  };

  const descriptionKey = label === REQUIREMENT_NAME.VCS_PERCENT ? "vcsDesc" : "ppvDesc";

  return (
    <div className={"DonutProgressCircle"} ref={ref}>
      <div className={"DonutProgressCircle__chart"} data-testid={"donutProgressCircle"}>
        <ChartDonut
          themeColor={ChartThemeColor.gray}
          constrainToVisibleArea
          data={data}
          labelComponent={
            <ChartTooltip
              style={{ fontSize: 10 }}
              flyoutStyle={{ fill: "#000" }}
              flyoutPadding={4}
              pointerLength={0}
              constrainToVisibleArea={true}
            />
          }
          innerRadius={90}
          cornerRadius={15}
          padAngle={0}
          colorScale={[currentPercentage === 1 ? greenColor : orangeColor, greyColor]}
          animate={animationDuration > 0 ? animationSettings : false}
          ariaTitle={ariaTitle}
          padding={0}
        />
      </div>
      <div className="DonutProgressCircle__label" data-testid={"DonutProgressCircle-label"}>
        <div className="DonutProgressCircle__label--title">{getTitle(label)}</div>
        <div className="DonutProgressCircle__label--text">
          <Trans>
            {t(`donutChart.${descriptionKey}`, defaultContent.donutChart[descriptionKey], {
              currentAmount: currentAmountFormatted ?? currentAmount,
              targetAmount: targetAmountFormatted ?? targetAmount,
            })}
            <span className={`DonutProgressCircle__label--text--${met ? "green" : "orange"}`}></span>
          </Trans>
        </div>
      </div>
    </div>
  );
};

export default DonutProgressCircle;
